import React from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { AvailableSlotInfo } from '../types';
import { useTheme } from '../theme/ThemeContext';
import { COLORS, getThemeStyles } from '../theme/designSystem';

interface Props {
  slots: AvailableSlotInfo[];
  selectedSlot: string | null;
  onSelectSlot: (time: string) => void;
}

export const SlotPicker: React.FC<Props> = ({ slots, selectedSlot, onSelectSlot }) => {
  const { isDark } = useTheme();
  const theme = getThemeStyles(isDark);

  if (slots.length === 0) {
    return (
      <View style={styles.emptyBox}>
        <Text style={styles.emptyText}>
          ⚠️ No slots available for the selected schedule. Please select another date.
        </Text>
      </View>
    );
  }

  return (
    <View style={{ marginVertical: 8 }}>
      <Text style={[styles.sectionTitle, { color: theme.textPrimary }]}>
        Select Available Time Slot (30m intervals)
      </Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingVertical: 4 }}>
        {slots.map((slot) => {
          const isSelected = selectedSlot === slot.time;
          const isDisabled = !slot.available;

          let btnBg = theme.cardBg;
          let borderColor = theme.border;
          let textColor = theme.textPrimary;
          let subtextColor = theme.textSecondary;

          if (isSelected) {
            btnBg = COLORS.primary;
            borderColor = COLORS.primary;
            textColor = '#FFFFFF';
            subtextColor = '#DBEAFE';
          } else if (isDisabled) {
            btnBg = isDark ? '#1E293B' : '#F1F5F9';
            borderColor = theme.border;
            textColor = '#94A3B8';
            subtextColor = '#CBD5E1';
          }

          return (
            <TouchableOpacity
              key={slot.time}
              disabled={isDisabled}
              onPress={() => onSelectSlot(slot.time)}
              style={[
                styles.slotItem,
                {
                  backgroundColor: btnBg,
                  borderColor: borderColor,
                  opacity: isDisabled ? 0.45 : 1,
                },
              ]}
            >
              <Text
                style={[
                  styles.slotTime,
                  { color: textColor, textDecorationLine: isDisabled ? 'line-through' : 'none' },
                ]}
              >
                {slot.time}
              </Text>
              <Text style={[styles.slotCap, { color: subtextColor }]}>
                {slot.bookedCount}/{slot.maxCapacity} Booked
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  emptyBox: {
    padding: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: COLORS.warning,
    backgroundColor: COLORS.warningBg,
    marginVertical: 8,
  },
  emptyText: {
    color: COLORS.warning,
    fontSize: 12,
    fontWeight: '700',
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 8,
  },
  slotItem: {
    marginRight: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 14,
    borderWidth: 1,
    alignItems: 'center',
    minWidth: 90,
  },
  slotTime: {
    fontSize: 14,
    fontWeight: '700',
  },
  slotCap: {
    fontSize: 10,
    marginTop: 2,
    fontWeight: '600',
  },
});
